import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Stack,
  Button,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../LayoutContainers/AppLayout/redux/AppSlice";
import AppService from "../../../Configurations/Appservice";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { AccountBalance, Pending, Payments } from "@mui/icons-material";
import pxToRem from "../../../Themes/Functions/pxToRem";
import moment from "moment";

// Helper function to format currency
const formatCurrency = (amount, currency = 'AED') => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-AE', { style: 'currency', currency }).format(amount);
};

// Metric Tile Component - matching PaymentDashboard design
const MetricTile = ({ title, amount, subtitle, icon: Icon, color }) => (
  <Card
    sx={{
      background: color,
      borderRadius: 2,
      p: 2.5,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      minHeight: 130
    }}
  >
    {/* Icon in top right */}
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        opacity: 0.15,
        color: 'white'
      }}
    >
      <Icon sx={{ fontSize: 42 }} />
    </Box>

    {/* Content */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'rgba(255, 255, 255, 0.95)',
          fontWeight: 500,
          mb: 1.5,
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="h4"
        sx={{
          color: '#fff',
          fontWeight: 700,
          mb: 0.75,
          fontSize: '1.4rem',
          lineHeight: 1.2
        }}
      >
        {amount}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.85)',
          fontSize: '0.65rem',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  </Card>
);

const CommissionDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [bankAccount, setBankAccount] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateScreenTitle("Commission Dashboard"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get organization ID from localStorage
      const authInfo = localStorage.getItem('authInfo');
      let organizationId = null;
      if (authInfo) {
        const parsed = JSON.parse(authInfo);
        organizationId = parsed?.principalInfo?.organisationId;
      }

      // Fetch pending payments, transaction history, and bank account
      const [pendingResponse, historyResponse, bankAccountResponse] = await Promise.all([
        AppService.get('v1/channel-partner-payments/my-pending-approvals').catch(err => {
          console.error('Error fetching pending approvals:', err);
          return [];
        }),
        organizationId
          ? AppService.get(`v1/channel-partner-payments/by-partner/${organizationId}`).catch(err => {
              console.error('Error fetching transaction history:', err);
              return [];
            })
          : Promise.resolve([]),
        organizationId
          ? AppService.get(`v1/partner-bank-accounts/partner/${organizationId}/primary`).catch(() => null)
          : Promise.resolve(null)
      ]);

      setPendingPayments(Array.isArray(pendingResponse) ? pendingResponse : pendingResponse?.data || []);
      setTransactionHistory(Array.isArray(historyResponse) ? historyResponse : historyResponse?.data || []);
      setBankAccount(bankAccountResponse);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data");
      setPendingPayments([]);
      setTransactionHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle view details
  const handleViewDetails = (payment, viewOnly = false) => {
    setSelectedPayment(payment);
    setIsViewOnly(viewOnly);
    setShowRejectInput(false);
    setRejectReason('');
    setDetailsOpen(true);
  };

  // Handle approve
  const handleApprove = async () => {
    if (!selectedPayment) return;
    setProcessing(true);
    setError(null);
    try {
      await AppService.post(`v1/channel-partner-payments/${selectedPayment.id}/approve`, {
        notes: null
      });
      setDetailsOpen(false);
      fetchData();
    } catch (err) {
      console.error('Error approving payment:', err);
      setError(err?.response?.data?.message || 'Failed to approve payment');
    } finally {
      setProcessing(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!selectedPayment) return;
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    setError(null);
    try {
      await AppService.post(`v1/channel-partner-payments/${selectedPayment.id}/reject?rejectionReason=${rejectReason}`);
      setDetailsOpen(false);
      setRejectReason('');
      setShowRejectInput(false);
      fetchData();
    } catch (err) {
      console.error('Error rejecting payment:', err);
      setError(err?.response?.data?.message || 'Failed to reject payment');
    } finally {
      setProcessing(false);
    }
  };

  // Calculate summary
  const pendingCount = pendingPayments.filter(p => p.status === 'PENDING_APPROVAL').length;
  const totalPendingAmount = pendingPayments
    .filter(p => p.status === 'PENDING_APPROVAL')
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);

  // Get total received from bank account totalBalance
  const totalReceived = bankAccount?.totalBalance || 0;

  // Get status chip
  const getStatusChip = (status) => {
    const statusConfig = {
      'CONFIRMED': { label: 'Confirmed', color: 'success' },
      'APPROVED': { label: 'Approved', color: 'success' },
      'PENDING_APPROVAL': { label: 'Pending', color: 'warning' },
      'REJECTED': { label: 'Rejected', color: 'error' }
    };
    const config = statusConfig[status] || { label: status || 'Unknown', color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Payment Item Component for list view
  const PaymentItem = ({ item, viewOnly = false }) => {
    const ticketCount = item.ticketCount || item.tickets?.length || 0;

    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, overflow: 'hidden' }}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          sx={{
            py: 1.5,
            px: 2,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
          onClick={() => handleViewDetails(item, viewOnly)}
        >
          {/* Date */}
          <Box sx={{ width: '12%' }}>
            <Typography fontSize={13} fontWeight={500}>
              {item.createdAt ? moment(item.createdAt).format('YYYY-MM-DD') : '—'}
            </Typography>
          </Box>
          {/* Reference */}
          <Box sx={{ width: '20%' }}>
            <Typography fontSize={14} fontWeight={600} color="primary.main">
              {item.referenceNumber || '—'}
            </Typography>
            {ticketCount > 0 && (
              <Typography fontSize={11} color="text.secondary">
                {ticketCount} ticket{ticketCount > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
          {/* From Bank */}
          <Box sx={{ width: '18%' }}>
            <Typography fontSize={12} color="text.secondary" noWrap>
              {item.sourceBankAccountName || '—'}
            </Typography>
          </Box>
          {/* To Bank */}
          <Box sx={{ width: '18%' }}>
            <Typography fontSize={12} color="text.secondary" noWrap>
              {item.partnerBankAccountName || '—'}
            </Typography>
          </Box>
          {/* Amount */}
          <Box sx={{ width: '15%', textAlign: 'right' }}>
            <Typography fontSize={14} fontWeight={600} color="primary.main">
              {formatCurrency(item.amount)}
            </Typography>
          </Box>
          {/* Status */}
          <Box sx={{ width: '17%', textAlign: 'right' }}>
            {getStatusChip(item.status)}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${pxToRem(55)})`,
        width: '100%',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Metric Tiles */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          {/* Bank Account Balance */}
          <Grid item xs={12} sm={6} md={4}>
            <MetricTile
              title="Bank Account"
              amount={bankAccount ? bankAccount.bankName || 'Active' : 'Not Set'}
              subtitle={
                bankAccount
                  ? `${bankAccount.accountHolderName || ''} • ****${bankAccount.accountNumber?.slice(-4) || ''}`
                  : 'Configure bank account'
              }
              icon={AccountBalance}
              color="#1a7f8f"
            />
          </Grid>

          {/* Pending Confirmation */}
          <Grid item xs={12} sm={6} md={4}>
            <MetricTile
              title="Pending Confirmation"
              amount={formatCurrency(totalPendingAmount)}
              subtitle={`${pendingCount} transaction${pendingCount !== 1 ? 's' : ''} awaiting approval`}
              icon={Pending}
              color="#1f6b7a"
            />
          </Grid>

          {/* Total Received */}
          <Grid item xs={12} sm={6} md={4}>
            <MetricTile
              title="Total Received"
              amount={formatCurrency(totalReceived)}
              subtitle="Total balance from commissions"
              icon={Payments}
              color="#16697A"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Tabs Section */}
      <Box sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ px: 2 }}
              >
                <Tab label="PENDING CONFIRMATION" />
                <Tab label="TRANSACTION HISTORY" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {/* Tab 0: Pending Confirmation */}
              {activeTab === 0 && (
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">Payments Awaiting Your Confirmation</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review and confirm commission payments from Thaklis
                    </Typography>
                  </Box>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                      <CircularProgress />
                    </Box>
                  ) : pendingPayments.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                      <Typography color="text.secondary">
                        No pending payments to confirm
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Table Header */}
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        sx={{ py: 1, px: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}
                      >
                        <Box sx={{ width: '12%' }}>
                          <Typography fontSize={12} fontWeight={600}>Date</Typography>
                        </Box>
                        <Box sx={{ width: '20%' }}>
                          <Typography fontSize={12} fontWeight={600}>Reference</Typography>
                        </Box>
                        <Box sx={{ width: '18%' }}>
                          <Typography fontSize={12} fontWeight={600}>From Bank</Typography>
                        </Box>
                        <Box sx={{ width: '18%' }}>
                          <Typography fontSize={12} fontWeight={600}>To Bank</Typography>
                        </Box>
                        <Box sx={{ width: '15%', textAlign: 'right' }}>
                          <Typography fontSize={12} fontWeight={600}>Amount</Typography>
                        </Box>
                        <Box sx={{ width: '17%', textAlign: 'right' }}>
                          <Typography fontSize={12} fontWeight={600}>Status</Typography>
                        </Box>
                      </Box>

                      {/* Payment List */}
                      <Stack sx={{ overflow: 'auto', flexGrow: 1 }}>
                        {pendingPayments.map((payment) => (
                          <PaymentItem key={payment.id} item={payment} viewOnly={false} />
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              )}

              {/* Tab 1: Transaction History */}
              {activeTab === 1 && (
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">Transaction History</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Complete history of all commission payments
                    </Typography>
                  </Box>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                      <CircularProgress />
                    </Box>
                  ) : transactionHistory.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No transaction history available
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Table Header */}
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        sx={{ py: 1, px: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}
                      >
                        <Box sx={{ width: '12%' }}>
                          <Typography fontSize={12} fontWeight={600}>Date</Typography>
                        </Box>
                        <Box sx={{ width: '20%' }}>
                          <Typography fontSize={12} fontWeight={600}>Reference</Typography>
                        </Box>
                        <Box sx={{ width: '18%' }}>
                          <Typography fontSize={12} fontWeight={600}>From Bank</Typography>
                        </Box>
                        <Box sx={{ width: '18%' }}>
                          <Typography fontSize={12} fontWeight={600}>To Bank</Typography>
                        </Box>
                        <Box sx={{ width: '15%', textAlign: 'right' }}>
                          <Typography fontSize={12} fontWeight={600}>Amount</Typography>
                        </Box>
                        <Box sx={{ width: '17%', textAlign: 'right' }}>
                          <Typography fontSize={12} fontWeight={600}>Status</Typography>
                        </Box>
                      </Box>

                      {/* Transaction List */}
                      <Stack sx={{ overflow: 'auto', flexGrow: 1 }}>
                        {transactionHistory.map((transaction) => (
                          <PaymentItem key={transaction.id} item={transaction} viewOnly={true} />
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Payment Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={(_, reason) => {
          if (processing) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setDetailsOpen(false);
          setShowRejectInput(false);
          setRejectReason('');
        }}
        disableEscapeKeyDown={processing}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#1a7f8f', color: 'white' }}>
          Payment Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Reference Number</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedPayment.referenceNumber || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Amount</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCurrency(selectedPayment.amount)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">From Bank</Typography>
                  <Typography variant="body1">
                    {selectedPayment.sourceBankAccountName || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">To Bank</Typography>
                  <Typography variant="body1">
                    {selectedPayment.partnerBankAccountName || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                  <Typography variant="body1">
                    {selectedPayment.createdAt ? moment(selectedPayment.createdAt).format('YYYY-MM-DD HH:mm') : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusChip(selectedPayment.status)}
                  </Box>
                </Box>
                {selectedPayment.tickets && selectedPayment.tickets.length > 0 && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">Tickets ({selectedPayment.tickets.length})</Typography>
                    <Box sx={{ mt: 1 }}>
                      {selectedPayment.tickets.map((ticket, idx) => (
                        <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                          #{ticket.ticketNumber} - {ticket.serviceName || '—'} ({formatCurrency(ticket.commissionAmount)})
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {!isViewOnly && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  By approving, you acknowledge that you have received this payment in your bank account.
                </Alert>
              )}

              {error && detailsOpen && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
          {!isViewOnly && showRejectInput && (
            <Box sx={{ width: '100%', mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Rejection Reason *"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                multiline
                rows={2}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            <Button onClick={() => { setDetailsOpen(false); setShowRejectInput(false); setRejectReason(''); }} disabled={processing}>Close</Button>
            {!isViewOnly && (
              <>
                {showRejectInput ? (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleReject}
                    disabled={processing || !rejectReason.trim()}
                  >
                    {processing ? <CircularProgress size={20} /> : 'Confirm Reject'}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowRejectInput(true)}
                  >
                    Reject
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleApprove}
                  disabled={processing}
                >
                  {processing ? <CircularProgress size={20} /> : 'Approve'}
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
        {processing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography sx={{ fontSize: '0.9rem', color: '#444', fontWeight: 500 }}>
              {showRejectInput ? 'Rejecting payment… please wait' : 'Approving payment… please wait'}
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default CommissionDashboard;
